import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { toast } from "sonner";
import {
  postWaitlist,
  sendWaitlistReadyText,
  seatWaitlistAsWalkIn,
  cancelWaitlistItem,
  revertWaitlistItem,
  deleteWaitlistItem,
} from "api";
import WaitlistItem from "./WaitlistItem";
import NewWaitlistForm from "./NewWaitlistForm";
import ContextMenu from "components/ContextMenu";

export default function WaitlistPage({
  active,
  cancelled,
  setActive,
  setCancelled,
  onSeated,
  onFormOpenChange,
}) {
  const [showAdd, setShowAdd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);

  useEffect(() => {
    onFormOpenChange?.(showAdd);
    return () => onFormOpenChange?.(false);
  }, [showAdd, onFormOpenChange]);

  async function handleAdd(form) {
    setSubmitting(true);
    try {
      const created = await postWaitlist({ ...form, user: "Employee" });
      setActive((prev) =>
        [...prev, created].sort((a, b) => new Date(a.dateMade) - new Date(b.dateMade))
      );
      setShowAdd(false);
      toast.success("Added to waitlist");
    } catch (err) {
      toast.error("Failed to add to waitlist");
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSendText(entry) {
    try {
      const updated = await sendWaitlistReadyText(entry._id);
      setActive((prev) => prev.map((e) => (e._id === updated._id ? updated : e)));
      toast.success("Text sent");
    } catch (err) {
      toast.error("Failed to send text");
      console.error(err);
    }
  }

  async function handleSeat(entry) {
    try {
      const result = await seatWaitlistAsWalkIn(entry._id, "Employee");
      if (result?.entry) {
        setActive((prev) => prev.map((e) => (e._id === result.entry._id ? result.entry : e)));
      }
      if (result?.reservation) onSeated?.(result.reservation);
      toast.success("Seated as walk-in");
      return result;
    } catch (err) {
      toast.error("Failed to seat walk-in");
      console.error(err);
    }
  }

  async function handleRemove(entry) {
    try {
      const updated = await cancelWaitlistItem(entry._id, "Employee");
      setActive((prev) => prev.filter((e) => e._id !== entry._id));
      setCancelled((prev) =>
        [updated, ...prev.filter((e) => e._id !== entry._id)].sort(
          (a, b) => new Date(b.dateMade) - new Date(a.dateMade)
        )
      );
    } catch (err) {
      toast.error("Failed to remove from waitlist");
      console.error(err);
    }
  }

  async function handleDelete(entry) {
    try {
      await deleteWaitlistItem(entry._id);
      setActive((prev) => prev.filter((e) => e._id !== entry._id));
      setCancelled((prev) => prev.filter((e) => e._id !== entry._id));
      toast.success("Waitlist entry deleted");
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete waitlist entry");
    }
  }

  function openContextMenu(e, entry) {
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      items: [
        {
          label: "Delete",
          danger: true,
          onSelect: () => handleDelete(entry),
        },
      ],
    });
  }

  async function handleRevert(entry) {
    try {
      const updated = await revertWaitlistItem(entry._id, "Employee");
      setCancelled((prev) => prev.filter((e) => e._id !== entry._id));
      setActive((prev) =>
        [...prev.filter((e) => e._id !== entry._id), updated].sort(
          (a, b) => new Date(a.dateMade) - new Date(b.dateMade)
        )
      );
      toast.success("Waitlist entry reverted");
    } catch (err) {
      toast.error("Failed to revert waitlist entry");
      console.error(err);
    }
  }

  const list = [...active, ...cancelled].sort(
    (a, b) => new Date(a.dateMade) - new Date(b.dateMade)
  );

  return (
    <div className="waitlist-page">
      <div className="waitlist-header">
        <div className="waitlist-header-counts">
          <div className="res-amount res-amount-header">
            <span style={{ minWidth: "1.6rem", display: "inline-block", textAlign: "center" }}>
              {active.filter((e) => e.state !== "seated").length}
            </span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 19.5C4 18.837 4.26339 18.2011 4.73223 17.7322C5.20107 17.2634 5.83696 17 6.5 17H20" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M6.5 2H20V22H6.5C5.83696 22 5.20107 21.7366 4.73223 21.2678C4.26339 20.7989 4 20.163 4 19.5V4.5C4 3.83696 4.26339 3.20107 4.73223 2.73223C5.20107 2.26339 5.83696 2 6.5 2V2Z" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="res-amount res-amount-header">
            <span style={{ minWidth: "1.6rem", display: "inline-block", textAlign: "center" }}>
              {active.filter((e) => e.state !== "seated").reduce((sum, e) => sum + (e.numGuests || 0), 0)}
            </span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M16 3.13C16.8604 3.3503 17.623 3.8507 18.1676 4.55231C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89317 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>
        <h2 className="waitlist-title">Waitlist</h2>
        <div className="waitlist-header-actions">
          <button
            type="button"
            className="waitlist-new-btn"
            onClick={() => setShowAdd((s) => !s)}
            aria-label={showAdd ? "Close new entry" : "Add to waitlist"}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              {showAdd ? (
                <path d="M18 6L6 18M6 6L18 18" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              ) : (
                <>
                  <path d="M12 5V19" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M5 12H19" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </>
              )}
            </svg>
          </button>
        </div>
      </div>

      <div className="waitlist-body">
        <AnimatePresence mode="wait">
          {showAdd ? (
            <NewWaitlistForm
              key="form"
              onSubmit={handleAdd}
              submitting={submitting}
            />
          ) : (
            <motion.div
              key="list"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="waitlist-list"
            >
              {list.length === 0 ? (
                <div className="waitlist-empty">
                  No one on the waitlist. Tap the + button to add a party.
                </div>
              ) : (
                list.map((entry) => (
                  <WaitlistItem
                    key={entry._id}
                    entry={entry}
                    onSendText={handleSendText}
                    onSeat={handleSeat}
                    onRemove={handleRemove}
                    onRevert={handleRevert}
                    onContextMenu={openContextMenu}
                  />
                ))
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          items={contextMenu.items}
          onClose={() => setContextMenu(null)}
        />
      )}
    </div>
  );
}
